import { HttpErrorResponse } from '@angular/common/http';
import { ChangeDetectionStrategy, Component, OnInit } from '@angular/core';
import {
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { SvgIconComponent } from 'angular-svg-icon';
import { catchError, Observable, of, take } from 'rxjs';
import { ButtonComponent } from '../../../aptem-component-library/components/button/button.component';
import { TextInputComponent } from '../../../aptem-component-library/components/text-input/text-input.component';
import { FormPageComponent } from '../../common/form/form.component';
import { SignupApiService } from '../../services/signup-api.service';
import { TokenService } from '../../services/token.service';

@Component({
  selector: 'complete-signup',
  templateUrl: './complete-signup.component.html',
  styleUrls: ['./complete-signup.component.scss'],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    SvgIconComponent,
    ButtonComponent,
    FormPageComponent,
    TextInputComponent,
    ReactiveFormsModule,
    FormsModule,
  ],
})
export class CompleteSignUpPageComponent implements OnInit {
  public landingForm: FormGroup;
  protected isSubmitting: boolean = false;
  protected pageHeadingKey: string = 'registration-page-heading';
  protected pageHeadingValue$: Observable<string | null>;
  protected pageBodyKey: string = 'registration-page-body';
  protected pageBodyValue$: Observable<string | null>;
  private token: string | null;
  protected orgCode: string = 'acme-inc';
  protected orgCodeControl = new FormControl('', {
    nonNullable: true,
    validators: [
      Validators.required,
      Validators.maxLength(20),
      Validators.pattern(/^[a-z0-9-]+$/),
    ],
  });

  constructor(
    private apiService: SignupApiService,
    private router: Router,
    private route: ActivatedRoute,
    private tokenService: TokenService
  ) {
    this.landingForm = new FormGroup({
      fullName: new FormControl('', [
        Validators.required,
        Validators.maxLength(500),
        Validators.pattern(/^[a-zA-Z0-9-' ]+$/),
      ]),
      emailAddress: new FormControl('', [
        Validators.required,
        Validators.maxLength(500),
        Validators.email,
      ]),
      orgCode: this.orgCodeControl,
      orgName: new FormControl('', [
        Validators.required,
        Validators.maxLength(250),
        Validators.pattern(/^[a-zA-Z0-9-' ]+$/),
      ]),
      adminEmail1: new FormControl('', [
        Validators.maxLength(500),
        Validators.email,
      ]),
      adminEmail2: new FormControl('', [
        Validators.maxLength(500),
        Validators.email,
      ]),
      adminEmail3: new FormControl('', [
        Validators.maxLength(500),
        Validators.email,
      ]),
    });
  }

  ngOnInit(): void {
    this.token = this.route.snapshot.queryParamMap.get('t');
    if (this.token) {
      const decodedToken = this.tokenService.decodeToken(this.token);
      this.landingForm.get('emailAddress')?.setValue(decodedToken.sub);
      this.landingForm
        .get('fullName')
        ?.setValue(this.tokenService.getCustomClaim(this.token, 'name'));
      this.landingForm.get('emailAddress')?.disable();
      this.landingForm.get('fullName')?.disable();
    }
  }

  onSubmit(): void {
    if (this.landingForm.valid && !this.isSubmitting) {
      this.isSubmitting = true;
      this.processSubmit();
    } else {
      return;
    }
  }

  processSubmit(): void {
    this.isSubmitting = true;

    const formData = this.landingForm.getRawValue();

    this.apiService
      .completeSignUp(
        formData.fullName,
        formData.emailAddress,
        formData.orgCode,
        formData.orgName,
        this.token,
        formData.adminEmail1,
        formData.adminEmail2,
        formData.adminEmail3
      )
      .pipe(
        take(1),
        catchError((err: HttpErrorResponse) => {
          console.error(err);

          if (err.status === 400) {
            const message =
              typeof err.error.error === 'string'
                ? err.error.error
                : err.error?.message;

            const errorMessage =
              message === 'Token expired.'
                ? 'Your verification email expired. Please try again.'
                : 'There was an error processing your request. Please try again.';

            this.router.navigate(['/signup'], {
              state: { error: errorMessage },
            });
          } else {
            this.router.navigate(['/error'], {
              state: { error: 'Unable to signup for a free account.' },
            });
          }

          this.isSubmitting = false;
          return of(null);
        })
      )
      .subscribe((response) => {
        if (!response) return; // skip success handling if it was an error

        this.landingForm.reset();
        this.landingForm.disable();

        this.router.navigate(['/signup-completed']);
        this.isSubmitting = false;
      });
  }

  updateOrgCodeFromEmail(): void {
    const emailControl = this.landingForm.get('emailAddress');

    if (!emailControl?.valid) {
      return;
    }

    const emailAddress = emailControl.value;

    const domains = emailAddress.split('@')[1];

    const domain = domains.split('.')[0];

    this.landingForm.get('orgCode')?.setValue(domain.toLowerCase());
  }
}
